import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { ApiUrl } from "../../../ApiUrl";
import SelectStudentFeeModal from "../FeeStudentModal/SelectStudentFeeModal";

const StudentHostelDetailsCreate = () => {
  const navigate = useNavigate();

  // Input states
  const [studentId, setStudentId] = useState("");
  const [studentName, setStudentName] = useState("");
  const [showStudentModal, setShowStudentModal] = useState(false);

  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  const [selectedHostel, setSelectedHostel] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedBed, setSelectedBed] = useState(null);

  // Options states
  const [classOptions, setClassOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);

  const [hostelOptions, setHostelOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);

  // Fetch initial option lists
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Classes
        const courRes = await fetch(`${ApiUrl.apiurl}Class/GetAllClass`);
        const courData = await courRes.json();
        if (courData.message === "Success" || courData.message === "success") {
          setClassOptions(courData.data.map((item) => ({ value: item.id, label: item.classname })));
        }

        // Hostels
        const hostRes = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelMasterList/`);
        const hostData = await hostRes.json();
        if (hostData.message === "success") {
          setHostelOptions(hostData.data.map((item) => ({ value: item.id, label: item.hostel_name })));
        }

        // Room Types
        const rtRes = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomTypeList/`);
        const rtData = await rtRes.json();
        if (rtData.message === "success") {
          setRoomTypeOptions(rtData.data.map((item) => ({ value: item.id, label: item.type_name })));
        }
      } catch (error) {
        console.error("Error loading dropdown data:", error);
      }
    };
    fetchInitialData();
  }, []);

  // Sections dropdown based on selected Class
  useEffect(() => {
    if (!selectedClass) {
      setSectionOptions([]);
      setSelectedSection(null);
      return;
    }
    const fetchSections = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}ClassSectionBind/GetAllSectionBindWithClass/${selectedClass.value}`);
        const data = await response.json();
        if (data.message === "success") {
          setSectionOptions(data.data.map((item) => ({ value: item.section_id, label: item.sectionname })));
        }
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
  }, [selectedClass]);

  // Blocks dropdown
  useEffect(() => {
    if (!selectedHostel) {
      setBlockOptions([]);
      setSelectedBlock(null);
      return;
    }
    const fetchBlocks = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelBlockList/${selectedHostel.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          setBlockOptions(data.data.map((item) => ({ value: item.id, label: item.block_name })));
        }
      } catch (error) {
        console.error("Error fetching blocks:", error);
      }
    };
    fetchBlocks();
  }, [selectedHostel]);

  // Floors dropdown
  useEffect(() => {
    if (!selectedBlock) {
      setFloorOptions([]);
      setSelectedFloor(null);
      return;
    }
    const fetchFloors = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelFloorList/${selectedBlock.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          setFloorOptions(data.data.map((item) => ({ value: item.id, label: item.floor_name })));
        }
      } catch (error) {
        console.error("Error fetching floors:", error);
      }
    };
    fetchFloors();
  }, [selectedBlock]);

  // Rooms dropdown
  useEffect(() => {
    if (!selectedFloor || !selectedRoomType) {
      setRoomOptions([]);
      setSelectedRoom(null);
      return;
    }
    const fetchRooms = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelRoomList/${selectedFloor.value}/${selectedRoomType.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          setRoomOptions(data.data.map((item) => ({ value: item.id, label: item.room_no })));
        }
      } catch (error) {
        console.error("Error fetching rooms:", error);
      }
    };
    fetchRooms();
  }, [selectedFloor, selectedRoomType]);

  // Beds dropdown
  useEffect(() => {
    if (!selectedRoom) {
      setBedOptions([]);
      setSelectedBed(null);
      return;
    }
    const fetchBeds = async () => {
      try {
        const response = await fetch(`${ApiUrl.apiurl}HOSTEL/HostelBedList/${selectedRoom.value}/`);
        const data = await response.json();
        if (data.message === "success") {
          setBedOptions(
            data.data.map((item) => ({
              value: item.id,
              label: item.bed_fee_amount
                ? `${item.bed_no} - Fee ${item.bed_fee_amount}`
                : item.bed_no,
              bedFeeAmount: item.bed_fee_amount,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching beds:", error);
      }
    };
    fetchBeds();
  }, [selectedRoom]);

  const handleSelectStudent = (student) => {
    setStudentId(student.studentId || "");
    setStudentName(student.studentname || "");

    // Auto-fill class
    if (student.classId) {
      setSelectedClass({ value: student.classId, label: student.classname || "Class" });
    } else {
      setSelectedClass(null);
    }

    // Auto-fill section
    if (student.sectionId) {
      setSelectedSection({ value: student.sectionId, label: student.sectionname || "Section" });
    } else {
      setSelectedSection(null);
    }

    setShowStudentModal(false);
  };

  const handleSave = async () => {
    // Validation
    const activeSessionId = localStorage.getItem("academicSessionId");
    if (
      !studentId ||
      !selectedClass ||
      !selectedSection ||
      !selectedHostel ||
      !selectedBlock ||
      !selectedFloor ||
      !selectedRoomType ||
      !selectedRoom ||
      !selectedBed
    ) {
      alert("Please fill out all fields.");
      return;
    }

    if (!activeSessionId) {
      alert("Academic session is missing. Please select a session from the top bar and try again.");
      return;
    }

    const payload = {
      student: studentId,
      session: activeSessionId,
      course: selectedClass.value, // maps to course (Class model) on backend
      branch: null,
      academic_year: null,
      semester: null,
      section: selectedSection.value,
      hostel: selectedHostel.value,
      block: selectedBlock.value,
      floor: selectedFloor.value,
      room_type: selectedRoomType.value,
      room: selectedRoom.value,
      bed: selectedBed.value,
    };

    try {
      const response = await fetch(`${ApiUrl.apiurl}HOSTEL/StudentHostelAssignmentCreate/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.message === "success") {
        alert("Student hostel details saved successfully!");
        navigate("/admin/student-hostel-details");
      } else {
        alert(data.message || "Failed to save hostel details.");
      }
    } catch (error) {
      console.error("Error saving hostel assignment:", error);
      alert("An error occurred while saving. Please try again.");
    }
  };

  const handleClose = () => {
    navigate("/admin/student-hostel-details");
  };

  const handleClear = () => {
    setStudentId("");
    setStudentName("");
    setShowStudentModal(false);

    setSelectedClass(null);
    setSelectedSection(null);

    setSelectedHostel(null);
    setSelectedBlock(null);
    setSelectedFloor(null);
    setSelectedRoomType(null);
    setSelectedRoom(null);
    setSelectedBed(null);

    setSectionOptions([]);
    setBlockOptions([]);
    setFloorOptions([]);
    setRoomOptions([]);
    setBedOptions([]);
  };

  const pageTitleStyle = {
    marginBottom: "0px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "700",
    color: "#000000",
  };

  const actionButtonStyle = {
    width: "150px",
  };

  const formPanelStyle = {
    backgroundColor: "#ffffff",
    borderTop: "3px solid #0d6efd",
    border: "1px solid #ccc",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.25)",
    borderRadius: "4px",
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "38px",
      fontSize: "14px",
    }),
  };

  return (
    <div className="container-fluid mt-3" style={{ backgroundColor: "#ffffff" }}>
      <div className="row">
        <div className="col-12">
          <div className="card p-0 shadow-sm border-0">
            <div className="card-body p-3">
              <p style={pageTitleStyle}>
                STUDENT HOSTEL DETAILS CREATE
              </p>

              {/* Action Buttons Row */}
              <div className="row mb-3 mt-3 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={actionButtonStyle}
                    onClick={handleSave}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={actionButtonStyle}
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={actionButtonStyle}
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </div>

              {/* Form Container */}
              <div className="row mx-0 mb-4 p-3" style={formPanelStyle}>
                {/* Row 1 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Student Name <span className="text-danger">*</span></label>
                  <div className="d-flex">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Student Name"
                      value={studentName}
                      readOnly
                    />
                    <button
                      type="button"
                      className="btn btn-primary ms-2"
                      onClick={() => setShowStudentModal(true)}
                      style={{ padding: "0 15px", height: "38px" }}
                    >
                      ...
                    </button>
                  </div>
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Class <span className="text-danger">*</span></label>
                  <Select
                    options={classOptions}
                    placeholder="Select Class"
                    value={selectedClass}
                    onChange={setSelectedClass}
                    isClearable
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Section <span className="text-danger">*</span></label>
                  <Select
                    options={sectionOptions}
                    placeholder="Select Section"
                    value={selectedSection}
                    onChange={setSelectedSection}
                    isClearable
                    isDisabled={!selectedClass}
                    styles={selectStyles}
                  />
                </div>

                {/* Row 2 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Hostel Name <span className="text-danger">*</span></label>
                  <Select
                    options={hostelOptions}
                    placeholder="Select Hostel"
                    value={selectedHostel}
                    onChange={setSelectedHostel}
                    isClearable
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Hostel Block <span className="text-danger">*</span></label>
                  <Select
                    options={blockOptions}
                    placeholder="Select Block"
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    isClearable
                    isDisabled={!selectedHostel}
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Floor No <span className="text-danger">*</span></label>
                  <Select
                    options={floorOptions}
                    placeholder="Select Floor"
                    value={selectedFloor}
                    onChange={setSelectedFloor}
                    isClearable
                    isDisabled={!selectedBlock}
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Room Type <span className="text-danger">*</span></label>
                  <Select
                    options={roomTypeOptions}
                    placeholder="Select Room Type"
                    value={selectedRoomType}
                    onChange={setSelectedRoomType}
                    isClearable
                    styles={selectStyles}
                  />
                </div>

                {/* Row 3 */}
                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Room No <span className="text-danger">*</span></label>
                  <Select
                    options={roomOptions}
                    placeholder="Select Room"
                    value={selectedRoom}
                    onChange={setSelectedRoom}
                    isClearable
                    isDisabled={!selectedFloor || !selectedRoomType}
                    styles={selectStyles}
                  />
                </div>

                <div className="col-12 col-md-3 mb-3">
                  <label className="form-label fw-bold">Bed No <span className="text-danger">*</span></label>
                  <Select
                    options={bedOptions}
                    placeholder="Select Bed No"
                    value={selectedBed}
                    onChange={setSelectedBed}
                    isClearable
                    isDisabled={!selectedRoom}
                    styles={selectStyles}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SelectStudentFeeModal
        show={showStudentModal}
        handleClose={() => setShowStudentModal(false)}
        onSelectStudent={handleSelectStudent}
        hostelAvailedOnly={true}
      />
    </div>
  );
};

export default StudentHostelDetailsCreate;
